import React, { useEffect } from 'react';
import useAutofillGuard from "./hooks/useAutofillGuard";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';

// Install global fetch interceptor that surfaces backend permission-denial 403s as toasts
import './utils/permissionDeniedInterceptor';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import CustomerLayout from './layouts/CustomerLayout';

// Standalone pages
import LoginPage from './LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TestPayment from './pages/TestPayment';
import PublicInspectionReport from './pages/PublicInspectionReport';
import PrivacyPolicy from './pages/admin/PrivacyPolicy';
import TermsOfService from './pages/admin/TermsOfService';
import PermissionDeniedToast from './components/PermissionDeniedToast';
import { Toast as GlobalToast } from './components/errors';
import { isPublicBookingPath, isPublicOrderPath, isPublicChatPath, isPublicFinishPath } from './utils/publicBookingPath';

// The website's Schedule Service form (/book, /book/card/<key>), shown on
// poseidonmarine.com in a frame: its own small tree, loaded on its own — no
// sign-in, no session refresh, no sockets for a website visitor.
const PublicBooking = React.lazy(() => import('./pages/PublicBooking'));
// The card page the AI receptionist texts (/card/<key>): loaded on its own too, so its
// look (shared with the booking form's card step) stays out of the app's bundle.
const PublicCardLink = React.lazy(() => import('./pages/PublicCardLink'));
const PublicVisit = React.lazy(() => import('./pages/PublicVisit'));
// The customer's link to an order (/o/<key>): on its own as well — no sign-in, no sockets.
const PublicOrder = React.lazy(() => import('./pages/PublicOrder'));
// The website chat window (/chat), framed by the chat bubble on poseidonmarine.com: on its own too.
const PublicChat = React.lazy(() => import('./pages/PublicChat'));
// NS Follow-Up's finish link (/f/<code>): on its own as well.
const PublicNsFinish = React.lazy(() => import('./pages/PublicNsFinish'));

// Main App wrapper with auth and router
function App() {
  // Password managers / Safari contact autofill stay off every field except
  // the sign-in forms (hooks/useAutofillGuard.js).
  useAutofillGuard();
  if (isPublicBookingPath(window.location.pathname)) {
    return (
      <React.Suspense fallback={null}>
        <PublicBooking />
      </React.Suspense>
    );
  }
  if (isPublicOrderPath(window.location.pathname)) {
    return (
      <React.Suspense fallback={null}>
        <PublicOrder />
      </React.Suspense>
    );
  }
  if (isPublicChatPath(window.location.pathname)) {
    return (
      <React.Suspense fallback={null}>
        <PublicChat />
      </React.Suspense>
    );
  }
  if (isPublicFinishPath(window.location.pathname)) {
    return (
      <React.Suspense fallback={null}>
        <PublicNsFinish />
      </React.Suspense>
    );
  }
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
          <PermissionDeniedToast />
          <GlobalToast />
          <Routes>
            <Route path="/test-payment" element={<TestPayment />} />
            <Route path="/inspection/:token" element={<PublicInspectionReport />} />
            {/* The card-on-file page the AI receptionist texts a caller — the signed link is the key. */}
            <Route path="/card/:token" element={<React.Suspense fallback={null}><PublicCardLink /></React.Suspense>} />
            <Route path="/visit/:token" element={<React.Suspense fallback={null}><PublicVisit /></React.Suspense>} />
            <Route path="/privacy" element={<PublicPrivacyPage />} />
            <Route path="/terms" element={<PublicTermsPage />} />
            <Route path="/login" element={<LoginPageWrapper />} />
            {/* /register and /signup land directly on the sign-up form so the
                page is reachable without authentication. Required by Twilio's
                campaign review — they probe these URLs to verify the SMS
                opt-in language is visible at the point of phone collection. */}
            <Route path="/register" element={<LoginPageWrapper />} />
            <Route path="/signup" element={<LoginPageWrapper />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* App routes (admin/employee dashboard) */}
            <Route path="/app/*" element={<AdminLayoutWrapper />} />
            {/* Customer routes */}
            <Route path="/portal/*" element={<CustomerLayoutWrapper />} />
            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Boot/loading screen. While the backend is unreachable the auth check
// RETRIES instead of logging the user out — say so, or a 20-second stall
// reads as a broken app.
function AuthLoading() {
  const { authRetrying } = useAuth();
  return (
    <div className="auth-loading">
      <div className="auth-loading-spinner"></div>
      <p>{authRetrying ? 'Reconnecting to Chariot… your session is safe.' : 'Loading...'}</p>
    </div>
  );
}

// Redirect root to appropriate dashboard based on auth
function RootRedirect() {
  const { isAuthenticated, loading, isCustomer } = useAuth();

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isCustomer) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  return <Navigate to="/app/dashboard" replace />;
}

// Public legal pages — render the same components as the Settings tabs,
// wrapped in minimal page chrome so they're reachable without login (for
// footer links, signup pages, external email).
function PublicPrivacyPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 20px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', background: '#fff', padding: 32, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <PrivacyPolicy />
      </div>
    </div>
  );
}

function PublicTermsPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 20px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', background: '#fff', padding: 32, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <TermsOfService />
      </div>
    </div>
  );
}

// Login page wrapper
function LoginPageWrapper() {
  const { isAuthenticated, loading, isCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      // A session-expired bounce carries the page the user was on — put
      // them back there instead of the default dashboard.
      const from = location.state?.from;
      if (isCustomer) {
        navigate(from && from.startsWith('/portal') ? from : '/portal/dashboard', { replace: true });
      } else {
        navigate(from && from.startsWith('/app') ? from : '/app/dashboard', { replace: true });
      }
    }
  }, [loading, isAuthenticated, isCustomer, navigate, location.state]);

  if (loading) {
    return <AuthLoading />;
  }

  if (isAuthenticated) {
    return null;
  }

  return <LoginPage />;
}

// Admin layout wrapper with auth protection
function AdminLayoutWrapper() {
  const { isAuthenticated, loading, user, logout, isCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Remember where they were — login sends them straight back.
      navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
    } else if (!loading && isCustomer) {
      navigate('/portal/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated, isCustomer, navigate]);

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated || isCustomer) {
    return null;
  }

  return <AdminLayout user={user} onLogout={logout} />;
}

// Customer layout wrapper with auth protection
function CustomerLayoutWrapper() {
  const { isAuthenticated, loading, user, logout, isCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
    } else if (!loading && !isCustomer) {
      navigate('/app/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated, isCustomer, navigate]);

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated || !isCustomer) {
    return null;
  }

  return <CustomerLayout user={user} onLogout={logout} />;
}

// App content - shows login or main dashboard based on role
function AppContent() {
  const { isAuthenticated, loading: authLoading, user, logout, isCustomer } = useAuth();

  if (authLoading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Route customers to customer portal, employees to main app
  if (isCustomer) {
    return <CustomerLayout user={user} onLogout={logout} />;
  }

  return <AdminLayout user={user} onLogout={logout} />;
}

export default App;
